import React, { useState, useEffect } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
} from "react-router-dom";
import { Toaster } from "react-hot-toast";
import axios from "axios";

import RecruiterDashboard from "./pages/RecruiterDashboard";
import MCQ from "./pages/MCQ";
import CandidateProgressPage from "./pages/CandidateProgressPage";
import CandidateProgressDashboard from "./pages/CandidateProgressDashboard";
import Login from "./pages/Login";
import UnauthorizedPage from "./pages/UnauthorizedPage";
import HomeRedirect from "./components/HomeRedirect";
import { ClipLoader } from "react-spinners";
import InvalidPage from "./pages/InvalidPage";
import CreateUser from "./pages/CreateUser";
import UserList from "./pages/UserList";
import Start from "./pages/Start";
import ChangePassword from "./pages/ChangePassword";
import EditUser from "./pages/EditUser";
import CandidateHrProgress from "./pages/CandidateHrProgress";
import UseAutoLogout from "./components/UseAutoLogout";
import Filters from "./pages/Filters";
import ForgotPassword from "./pages/ForgotPassword";

const App = () => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Check if this is a fresh session (tab was closed and reopened)
    const sessionActive = sessionStorage.getItem('sessionActive');
    const wasRefresh = performance.getEntriesByType('navigation')[0]?.type === 'reload';

    if (!sessionActive && !wasRefresh) {
      // This is a fresh session (tab was closed), logout the user
      const logoutUser = async () => {
        try {
          await axios.get(
            `${import.meta.env.VITE_REACT_APP_BACKEND_BASEURL}/api/v1/user/logout`,
            { withCredentials: true }
          );
        } catch (error) {
        }

        // Clear all storage
        localStorage.clear();
        sessionStorage.clear();

        // Set session as active for this new session
        sessionStorage.setItem('sessionActive', 'true');

        // Don't check auth, just finish loading
        setLoading(false);
      };

      logoutUser();
    } else {
      // Session is active or it's a refresh, check authentication normally
      sessionStorage.setItem('sessionActive', 'true');
      checkAuth();
    }
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_REACT_APP_BACKEND_BASEURL}/api/v1/user/get-user`,
        { withCredentials: true }
      );
      if (response.data.success) {
        setUser(response.data.user);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  UseAutoLogout(Boolean(user));

  const setLoggedInUser = (user) => {
    setUser(user);
    // When user logs in, mark session as active
    if (user) {
      sessionStorage.setItem('sessionActive', 'true');
    }
  }

  const ProtectedRoute = ({ element, allowedRoles }) => {
    if (loading) {
      return <ClipLoader className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />;
    }

    if (!user) {
      return <Navigate to="/login" replace />;
    }

    if (allowedRoles && !allowedRoles.includes(user.role)) {
      return <Navigate to="/unauthorized" state={{ message: "You don't have permission to access this page" }} replace />;
    }

    return element;
  };


  const router = createBrowserRouter([
    {
      path: "/",
      element: <HomeRedirect />,
    },
    {
      path: "/unauthorized",
      element: <UnauthorizedPage />,
    },
    {
      path: "/invalid",
      element: <InvalidPage />,
    },
    {
      path: "/create-user",
      element: (
        <ProtectedRoute element={<CreateUser currentUser={user} />} allowedRoles={["admin"]} />
      ),
    },
    {
      path: "/filters",
      element: (
        <ProtectedRoute element={<Filters currentUser={user} />} allowedRoles={["admin"]} />
      ),
    },
    {
      path: "/edit-user/:id/:name/:email/:role",
      element: (
        <ProtectedRoute element={<EditUser currentUser={user} />} allowedRoles={["admin"]} />
      ),
    },
    {
      path: "/users",
      element: (
        <ProtectedRoute
          element={<UserList currentUser={user} />}
          allowedRoles={["admin"]}
        />
      ),
    },
    {
      path: "/login",
      element: <Login setLoggedInUser={setLoggedInUser} />,
    },
    {
      path: "/forgot-password",
      element: <ForgotPassword />,
    },
    {
      path: "/recruiter-dashboard",
      element: (
        <ProtectedRoute
          element={<RecruiterDashboard currentUser={user} />}
          allowedRoles={["recruiter", "admin"]}
        />
      ),
    },
    {
      path: "/change-password",
      element: (
        <ProtectedRoute
          element={<ChangePassword currentUser={user} />}
          allowedRoles={["recruiter", "admin"]}
        />
      ),
    },
    {
      path: "/mcq/:candidateId",
      element: <MCQ />,
    },
    {
      path: "/start/:candidateId",
      element: <Start />,
    },
    {
      path: "/candidate-progress-page/:candidateId",
      element: (
        <ProtectedRoute
          element={<CandidateProgressPage currentUser={user} />}
          allowedRoles={["admin", "recruiter"]}
        />
      ),
    },
    {
      path: "/candidate-hr-progress/:candidateId",
      element: (
        <ProtectedRoute
          element={<CandidateHrProgress currentUser={user} />}
          allowedRoles={["admin", "recruiter"]}
        />
      ),
    },
    {
      path: "/candidate-progress-dashboard",
      element: (
        <ProtectedRoute
          element={<CandidateProgressDashboard currentUser={user} />}
          allowedRoles={["admin", "recruiter"]}
        />
      ),
    },
    {
      path: "*",
      element: <InvalidPage />,
    },
    {
      path: "/invalid",
      element: <InvalidPage />,
    },
  ]);

  return (
    <>
      <Toaster />
      {loading ? (
        <ClipLoader className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
      ) : (
        <RouterProvider router={router} />
      )}
    </>
  );
};

export default App;
